import {
  Box,
  Button,
  Container,
  CssBaseline,
  Grid,
  Paper,
  Typography,
} from "@material-ui/core";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";
import React, { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { useAsyncFn, useKeyPress } from "react-use";
import VerificationInput from "react-verification-input";
import logo from "../images/mobile-phone.png";

const useStyles = makeStyles((theme) => ({
  invalidOTP: {
    border: "1px solid #FA5252",
    backgroundColor: "#FFF5F5",
  },
  insidePaper: {
    marginTop: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
  },
  main: {
    marginTop: theme.spacing(7),
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    marginTop: 32,
    backgroundColor: "#5046B2",
    height: 50,
  },
  paper: {
    display: "flex",
    width: "100%",
    flexWrap: "wrap",
    "& > *": {
      margin: theme.spacing(1),
      // padding: theme.spacing(4),
    },
  },
  iconContainer: {
    display: "flex",
    width: 63,
    height: 63,
    background: "#E5DBFF",
    borderRadius: "50%",
    alignItems: "center",
    justifyContent: "center",
  },
  iconContainerOuter: {
    display: "flex",
    width: 96,
    height: 96,
    background: "#F3F0FF",
    borderRadius: "50%",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 38,
  },
  verificationInputContainer: {
    display: "flex",
    justifyContent: "center",
  },
}));

export default function ResetPasswordVerify() {
  const classes = useStyles();
  const params = useParams();
  const [otp, setOtp] = useState("");
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState("");
  const [isEnterPressed] = useKeyPress("Enter");
  const history = useHistory();

  const [verifyOtpState, postVerifyOtp] = useAsyncFn(async () => {
    const response = await axios.post("/auth/verify-otp", {
      otp,
      action: "FORGOT_PASSWORD",
      email: params.email,
    });
    return response.data;
  }, [otp, params.email]);

  const [resendOTP, postResendOTP] = useAsyncFn(async () => {
    const response = await axios.post("/auth/send-otp", {
      action: "FORGOT_PASSWORD",
      email: params.email,
    });

    return response.data;
  }, [otp, params.email]);

  useEffect(() => {
    if (resendOTP?.value?.success) {
      toast.success("Verification code has been sent to your email");
      return;
    }

    if (resendOTP?.error) {
      if (resendOTP?.error?.response?.data?.error) {
        setErrorMessage(resendOTP?.error?.response?.data?.error);
      } else {
        setErrorMessage(t("Internal error"));
      }
    }
  }, [resendOTP, t]);

  useEffect(() => {
    if (verifyOtpState?.value?.success) {
      const { token } = verifyOtpState.value.data;
      history.push(
        `/resetpassword/${encodeURIComponent(token)}/${params.email}`
      );
      return;
    }

    if (verifyOtpState?.error) {
      if (verifyOtpState?.error?.response?.data?.error) {
        setErrorMessage(resendOTP?.error?.response?.data?.error);
      } else {
        setErrorMessage(t("Internal error"));
      }
    }
  }, [
    verifyOtpState,
    history,
    params.email,
    resendOTP?.error?.response?.data?.error,
    t,
  ]);

  useEffect(() => {
    if (isEnterPressed) postVerifyOtp();
  }, [isEnterPressed, postVerifyOtp]);

  return (
    <>
      <Toaster />
      <Container
        component="main"
        maxWidth="xs"
        className={clsx(classes.main, "form-style")}
      >
        <CssBaseline />
        <Box
          maxWidth="xs"
          display="flex"
          alignItems="center"
          justifyContent="center"
          marginBottom="50px"
        >
          <img src={logo} alt="flyins" width="100" height="100" />
          <Grid item xs={12}>
            <Typography
              color="primary"
              style={{
                fontSize: "4rem",
                paddingLeft: "25px",
                fontFamily: "Carter One, cursive",
              }}
            >
              Flyins
            </Typography>
          </Grid>
        </Box>

        <div className={classes.paper}>
          <Paper elevation={2} style={{ width: "100%" }}>
            <div className={classes.insidePaper}>
              <Typography
                component="h1"
                variant="h4"
                style={{ marginBottom: 10, fontWeight: 500 }}
              >
                {t("Enter Verification Code")}
              </Typography>
              <Typography
                component="p"
                variant="caption"
                style={{
                  marginTop: 10,
                  marginBottom: 40,
                  fontSize: 14,
                  color: "#495057",
                }}
              >
                {t("email sent to first line")}{" "}
                <span style={{ fontWeight: "bold" }}>{params?.email}</span>.
                {t("email sent to second line")}
              </Typography>

              <Typography
                style={{ color: "red", marginBottom: 5, textAlign: "center" }}
              >
                {errorMessage}
              </Typography>
              <div className={classes.verificationInputContainer}>
                <VerificationInput
                  value={otp}
                  onChange={(value) => setOtp(value)}
                  placeholder=""
                  classNames={{
                    container: "container",
                    character: `character${`${
                      verifyOtpState.error && !verifyOtpState.loading
                        ? ` ${classes.invalidOTP}`
                        : ""
                    }`}`,
                    characterInactive: "character--inactive",
                    characterSelected: "character--selected",
                  }}
                />
              </div>
              {verifyOtpState?.error?.response?.data?.error && (
                <Typography
                  style={{
                    color: "red",
                    marginBottom: 5,
                    marginTop: 24,
                    justifyContent: "center",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <svg
                    width="16"
                    height="15"
                    viewBox="0 0 16 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M8 0L16 14.4H0L8 0ZM8 3.2946L13.2808 12.8H2.71922L8 3.2946ZM8.79922 5.59998H7.19922V6.87998H8.79922V5.59998ZM8.79922 7.5199H7.19922V11.9999H8.79922V7.5199Z"
                      fill="#EB5757"
                    />
                  </svg>
                  <span
                    style={{ marginLeft: 8, fontSize: 13, fontWeight: 400 }}
                  >
                    {verifyOtpState?.error?.response?.data?.error}
                  </span>
                </Typography>
              )}
              <Button
                disabled={verifyOtpState.loading || resendOTP.loading}
                onClick={() => {
                  setErrorMessage("");
                  postVerifyOtp();
                }}
                style={{ fontWeight: "bold" }}
                type="button"
                fullWidth
                variant="contained"
                color="primary"
                id="submit"
                className={classes.submit}
              >
                {verifyOtpState.loading ? t("confirming otp") : t("confirm")}
              </Button>
              <Button
                onClick={() => {
                  setErrorMessage("");
                  postResendOTP();
                }}
                disabled={verifyOtpState.loading || resendOTP.loading}
                style={{ fontWeight: "bold", marginTop: 24 }}
                type="button"
                fullWidth
                variant="text"
                color="primary"
                id="submit"
              >
                {resendOTP.loading ? t("resending code") : t("resend code")}
              </Button>
            </div>
          </Paper>
        </div>
      </Container>
    </>
  );
}
